import { FC, ReactElement, useMemo, useState } from "react";
import { format } from "date-fns";
import {
  Box,
  CircularProgress,
  Grid,
  Stack,
  Typography,
  InputAdornment,
} from "@mui/material";
import { Card, IconImage, SelectClientBanner } from "@/components";
import {
  formatDateOrTime,
  formatNumberWithSuffix,
  getLastYearToDateRange,
  responsiveFontSizes,
} from "@/utils";
import {
  useGetDashboardUpdateDateQuery,
  useGetAllVendorsQuery,
} from "@/services";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import {
  HeaderSearchBox,
  StyledTextInput,
} from "@/pages/COMMON/documents/styles";
import { search_img } from "@/constants";
// import { getTopManufacturers } from "./helpers";
import { AdvanceFilters, FirstSaleTable } from "./components";

const FirstSale: FC = (): ReactElement => {
  const { selectedClient, areClientsLoading } = useAppSelector(
    (state) => state.client
  );
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const { user } = useAppSelector((state) => state.auth);
  
  // Backend filters (sent to API)
  const [backendFilters, setBackendFilters] = useState<{
    dutyType: Attribute;
    dateRange: {
      startDate: Date;
      endDate: Date;
    };
  }>(() => {
    const { startDate, endDate } = getLastYearToDateRange();
    return {
      dutyType: "" as Attribute,
      dateRange: {
        startDate,
        endDate,
      },
    };
  });

  // Frontend filters (handled locally)
  const [frontendFilters, setFrontendFilters] = useState<{
    mismatchFilter: string;
    searchQuery: string;
  }>({
    mismatchFilter: "all",
    searchQuery: "",
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const userClientId = useMemo(() => {
    if (
      user?.userRole === roles.TDADMIN &&
      selectedTenant?.tenantType === "TRADEDIVE_CLIENT"
    ) {
      return selectedTenant?.users[0]?._id;
    }
    return user?.userRole === roles.TDCLIENT
      ? user?._id
      : selectedClient?._id || "";
  }, [user, selectedClient, selectedTenant]);

  const canSelectClients = useMemo(() => {
    const allowedRoles = [
      roles.SPOWNER,
      roles.SPPARTNER,
      roles.TDADMIN,
    ] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  const isTDAdminTCD = useMemo(() => {
    return (
      user?.userRole === roles.TDADMIN &&
      selectedTenant?.tenantType === "TRADEDIVE_CLIENT"
    );
  }, [user, selectedTenant]);

  const apiFilters = useMemo(() => {
    return {
      dateFrom: format(
        backendFilters.dateRange.startDate ?? new Date("2000-01-01"),
        "yyyy-MM-dd"
      ),
      dateTo: format(backendFilters.dateRange.endDate ?? new Date(), "yyyy-MM-dd"),
      clientId: selectedClient?._id,
      ...(isTDAdminTCD && { tenantId: selectedTenant?._id }),
      ...(backendFilters.dutyType && { attribute: backendFilters.dutyType }),
    };
  }, [backendFilters, selectedClient, selectedTenant, isTDAdminTCD]);

  const commonOptions = useMemo(() => {
    if (isTDAdminTCD) {
      return {
        skip: !selectedTenant,
        refetchOnMountOrArgChange: true,
      };
    }
    return {
      skip: canSelectClients && !selectedClient,
      refetchOnMountOrArgChange: true,
    };
  }, [canSelectClients, selectedClient, selectedTenant, isTDAdminTCD]);

  const { data: lastUpdated, isFetching: isFetchingDate } =
    useGetDashboardUpdateDateQuery(
      {
        params: {
          clientId: apiFilters.clientId,
          ...(isTDAdminTCD && {
            tenantId: selectedTenant?._id,
          }),
        },
      },
      commonOptions
    );

  const { data: allVendorsData, isLoading: isAllVendorsLoading, refetch: refetchAllVendors } =
    useGetAllVendorsQuery(
      {
        vendorsInput: {
          ...apiFilters,
        },
      },
      commonOptions
    );

  // Frontend filtering and search
  const filteredData = useMemo(() => {
    if (!allVendorsData?.data?.tradeData) return [];

    let filtered = [...allVendorsData.data.tradeData];

    // Search filter
    if (frontendFilters.searchQuery) {
      const searchLower = frontendFilters.searchQuery.toLowerCase();
      filtered = filtered.filter((item) => {
        const manufacturer = (item.manufacturer || "").toLowerCase();
        const manufacturerName = (item.manufacturerName || "").toLowerCase();
        return manufacturer.includes(searchLower) || manufacturerName.includes(searchLower);
      });
    }

    // Mismatch filter
    if (frontendFilters.mismatchFilter !== "all") {
      filtered = filtered.filter((item) => {
        const isExporter = item.isExporter;
        const isManufacturer = item.isManufacturer;

        switch (frontendFilters.mismatchFilter) {
          case "any":
            return isExporter || isManufacturer;
          case "origin-export":
            return isExporter;
          case "origin-manufacturer":
            return isManufacturer;
          case "none":
            return !isExporter && !isManufacturer;
          default:
            return true;
        }
      });
    }

    return filtered;
  }, [allVendorsData?.data?.tradeData, frontendFilters]);

  // Pagination
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, pageSize]);


  const dutyRateRange = useMemo(() => {
    if (!filteredData?.length) return { min: 0, max: 0 };

    const rates = filteredData?.map((item) =>
      parseFloat((item.customValue * 100).toFixed(2))
    );
    return {
      min: Math.min(...rates),
      max: Math.max(...rates),
    };
  }, [filteredData]);

  // Removed Top Manufacturers card and related computation

  const handleSearchChange = (value: string) => {
    setFrontendFilters(prev => ({ ...prev, searchQuery: value }));
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleAdvanceFiltersChange = ({
    dutyType,
    dateRange,
    mismatchFilter,
  }: {
    dutyType: Attribute;
    dateRange: { startDate: Date; endDate: Date };
    mismatchFilter: string;
  }) => {
    // Update backend filters
    setBackendFilters({
      dutyType,
      dateRange,
    });

    // Update frontend filters
    setFrontendFilters(prev => ({
      ...prev,
      mismatchFilter,
    }));

    setCurrentPage(1); // Reset to first page when filters change
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  if (isAllVendorsLoading || isFetchingDate || areClientsLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (canSelectClients && !selectedClient && !isTDAdminTCD) {
    return <SelectClientBanner />;
  }

  return (
    <Stack sx={{ p: { xs: 1.5, md: 2 } }}>
      <Typography variant="h4" fontWeight={600} sx={{ fontSize: responsiveFontSizes.lg, lineHeight: 1.2 }}>
        First Sale Opportunities
      </Typography>
      <Typography variant="body2" color="text.secondary" pb={2} sx={{ fontSize: responsiveFontSizes.xs }}>
        Last Updated: {formatDateOrTime(lastUpdated ?? new Date(), "both")}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <Card
            variant="kpi"
            sx={{ minHeight: { xs: 84, md: 96 } }}
            title="Total Potential Savings"
            subtitle={`$${formatNumberWithSuffix(
              (allVendorsData?.data?.total?.customsDuty || 0) * 0.1
            )}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <Card
            variant="kpi"
            sx={{ minHeight: { xs: 84, md: 96 } }}
            title="Total Vendors"
            subtitle={String(filteredData?.length)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <Card
            variant="kpi"
            sx={{ minHeight: { xs: 84, md: 96 } }}
            title="Duty Rate Range"
            subtitle={`${dutyRateRange.min}% - ${dutyRateRange.max}%`}
          />
        </Grid>
        {/* Removed Top Manufacturers card per request */}
      </Grid>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ mt: 3, mb: 2 }}
      >
        <HeaderSearchBox>
          <StyledTextInput
            type="text"
            placeholder="Search"
            sx={{ width: "320px" }}
            value={frontendFilters.searchQuery}
            handleChangeValue={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconImage src={search_img} size="auto" />
                </InputAdornment>
              ),
            }}
          />
        </HeaderSearchBox>
        <Box>
          <AdvanceFilters
            appliedFilters={{
              dutyType: backendFilters.dutyType,
              dateRange: backendFilters.dateRange,
              mismatchFilter: frontendFilters.mismatchFilter,
            }}
            onFiltersChange={handleAdvanceFiltersChange}
          />
        </Box>
      </Stack>
      {isAllVendorsLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      ) : (
        <FirstSaleTable
          data={paginatedData}
          userClientId={userClientId}
          onRefetch={refetchAllVendors}
          isLoading={isAllVendorsLoading}
          // Pagination props
          currentPage={currentPage}
          pageSize={pageSize}
          totalItems={filteredData.length}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
    </Stack>
  );
};

export default FirstSale;
